import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { saveStorage } from '../saveStorage';
import { RankService } from '../../services/rankService';
import { enqueueAchievementToast } from '../../ui/achievementToast';
import { ProgressService } from '../../services/progressService';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS, levelsInChapter } from '../../config/levels';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { COIN_SPIN_FRAMES, spinCoin } from '../coin';
import { drawStarfield } from '../starfield';
import { buildTabRow, LEADERBOARD_GREEN_HEX, LEADERBOARD_TAB, type TabId } from '../chapterTabs';
import { ScrollList } from '../../ui/scrollList';
import { drawAvatarIcon } from '../avatarIcon';
import {
  BOARD_KINDS,
  BOARD_LABELS,
  CAPY_RUN_BOARD,
  formatBoardValue,
  formatRank,
  type AnyBoardKind,
  type BoardKind,
  type LeaderboardRow,
} from '../../config/leaderboard';
import {
  fetchMyRank,
  fetchTopBoard,
  leaderboardAvailable,
  leaderboardSignedIn,
} from '../../account/leaderboardApi';

/**
 * LeaderboardScene — the fourth tab of the episode menu.
 *
 * Three boards, one shown at a time; switching sub-tab swaps the top 50 AND the
 * player's own rank together, because a player ranked 39th on coins is a
 * different number on score and showing the stale one would be a lie.
 *
 * There is deliberately NO affordance keyed to another player's name: no tap
 * target, no profile, no message. A name on this screen is a name and nothing
 * else — see docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §14.
 *
 * Spec: docs/superpowers/specs/2026-09-20-leaderboards-design.md
 */

/** Board column geometry — shared by the list rows and the player's own row. */
const BOARD_X = 200;
const BOARD_W = 780;
const ROW_H = 52;
const ROW_PITCH = 58;
/** The scrolling viewport. */
const LIST_TOP = 244;
const LIST_H = 446;

const AVATAR_R = 21;
const AVATAR_CX = 92;
const RANK_CX = 34;
const NAME_CX = BOARD_W / 2;
const VALUE_RIGHT = BOARD_W - 22;

/**
 * How the scene is opened. With no data it is the episode menu's fourth tab.
 * With `board: 'capyrun'` it is the Event Archive's Capy Run high-score board
 * (spec 2026-10-04): that one board alone — no chapter tabs, no sub-tabs, no
 * avatar buttons — and BACK returns to `returnTo` (the archive).
 */
export interface LeaderboardSceneData {
  board?: typeof CAPY_RUN_BOARD;
  returnTo?: string;
  returnData?: object;
}

export class LeaderboardScene extends Phaser.Scene {
  private progress!: ProgressService;
  private list?: ScrollList;
  private board: AnyBoardKind = 'coins';
  /** The Capy Run board on its own (opened from the end of an archive run). */
  private single = false;
  private returnTo = 'MenuScene';
  private returnData?: object;
  /** Guards against a slow request painting over a newer tab selection. */
  private requestSeq = 0;
  private subTabs: { kind: BoardKind; txt: Phaser.GameObjects.Text }[] = [];
  private statusText?: Phaser.GameObjects.Text;
  private ownRow?: Phaser.GameObjects.Container;
  private ownRowHint?: Phaser.GameObjects.Text;
  private signedIn = false;

  constructor() {
    super({ key: 'LeaderboardScene' });
  }

  init(data?: LeaderboardSceneData): void {
    this.single = data?.board === CAPY_RUN_BOARD;
    this.returnTo = this.single ? (data?.returnTo ?? 'HubScene') : 'MenuScene';
    this.returnData = this.single
      ? (data?.returnData ?? { tab: 'events', archive: true })
      : undefined;
    // Consume the data. Phaser keeps a scene's last start data and hands it to
    // the next `scene.start(key)` that passes none — so without this, the
    // episode menu's LEADERBOARDS tab would reopen as the Capy Run board.
    this.sys.settings.data = {};
  }

  create(): void {
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.subTabs = [];
    this.list = undefined;
    this.ownRow = undefined;
    this.board = this.single ? CAPY_RUN_BOARD : 'coins';

    drawStarfield(this, 1280, 720);

    this.add
      .text(640, 48, this.single ? 'CAPY RUN HIGH SCORES' : 'LEADERBOARDS', {
        fontSize: '38px',
        color: this.single ? '#e0b98a' : LEADERBOARD_GREEN_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setName('leaderboard-title');
    this.add
      .text(
        640,
        84,
        this.single
          ? 'THE EVENT ARCHIVE — MOST CAPYBARAS TAPPED IN ONE RUN'
          : 'THE TOP FIFTY HAMBI COMMANDERS',
        { fontSize: '15px', color: '#9aa6ad' },
      )
      .setOrigin(0.5);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    backBtn.setName('leaderboard-back');
    backBtn.on('pointerup', () => this.scene.start(this.returnTo, this.returnData));

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    const coinIcon = this.add.image(960, 50, 'coin').setDisplaySize(34, 34).setDepth(5);
    spinCoin(this, coinIcon);
    this.add
      .text(982, 50, coinLabel(this.progress.coins()), {
        fontSize: '20px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5);

    if (this.single) {
      // The one board's header, where the sub-tabs would sit.
      this.add
        .text(BOARD_X + BOARD_W / 2, 160, 'CAPY RUN', {
          fontSize: '17px',
          color: '#e0b98a',
          fontStyle: 'bold',
          backgroundColor: '#262234',
          padding: { x: 18, y: 8 },
        })
        .setOrigin(0.5)
        .setLetterSpacing(2)
        .setName(`board-tab-${CAPY_RUN_BOARD}`);
      // No avatar buttons here, but the own-row hint still needs to know.
      void leaderboardSignedIn().then((yes) => (this.signedIn = yes));
    } else {
      buildTabRow(this, {
        selected: LEADERBOARD_TAB,
        isChapterUnlocked: (c) => this.isChapterUnlocked(c),
        onSelect: (id) => this.onTab(id),
        onLocked: () => {
          /* a locked chapter from here simply does nothing */
        },
        showLeaderboards: leaderboardAvailable(),
      });

      this.buildSubTabs();
      this.buildEditAvatarButton();
    }

    this.list = new ScrollList(this, {
      x: BOARD_X,
      y: LIST_TOP,
      width: BOARD_W,
      height: LIST_H,
      barX: BOARD_X + BOARD_W + 18,
    });

    this.statusText = this.add
      .text(BOARD_X + BOARD_W / 2, LIST_TOP + 60, '', {
        fontSize: '18px',
        color: '#9aa6ad',
        fontStyle: 'italic',
      })
      .setOrigin(0.5)
      .setDepth(4);

    void this.refresh();

    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** Mirrors LevelSelectScene: chapter 1 always, others once their opener unlocks. */
  private isChapterUnlocked(chapter: number): boolean {
    const first = levelsInChapter(chapter)[0];
    return !first || chapter <= 1 || this.progress.isUnlocked(first.id);
  }

  private onTab(id: TabId): void {
    if (id === LEADERBOARD_TAB) return;
    this.scene.start('LevelSelectScene', { chapter: id });
  }

  // ── Sub-tabs ──────────────────────────────────────────────────────────────

  private buildSubTabs(): void {
    const pitch = 150;
    const cx = BOARD_X + BOARD_W / 2;
    let x = cx - ((BOARD_KINDS.length - 1) * pitch) / 2;
    for (const kind of BOARD_KINDS) {
      const txt = this.add
        .text(x, 160, BOARD_LABELS[kind], {
          fontSize: '17px',
          color: '#cfd3dc',
          fontStyle: 'bold',
          backgroundColor: '#15131f',
          padding: { x: 18, y: 8 },
        })
        .setOrigin(0.5)
        .setLetterSpacing(2)
        .setName(`board-tab-${kind}`)
        .setInteractive({ useHandCursor: true });
      txt.on('pointerup', () => this.onBoard(kind));
      this.subTabs.push({ kind, txt });
      x += pitch;
    }
    this.restyleSubTabs();
  }

  private restyleSubTabs(): void {
    for (const { kind, txt } of this.subTabs) {
      const on = kind === this.board;
      txt.setColor(on ? LEADERBOARD_GREEN_HEX : '#cfd3dc');
      txt.setBackgroundColor(on ? '#262234' : '#15131f');
    }
  }

  private onBoard(kind: BoardKind): void {
    if (kind === this.board) return;
    this.board = kind;
    this.restyleSubTabs();
    this.list?.scrollToTop();
    void this.refresh();
  }

  private buildEditAvatarButton(): void {
    const edit = this.add
      .text(1105, 208, '✎ EDIT AVATAR', {
        fontSize: '15px',
        color: '#0c0b16',
        backgroundColor: LEADERBOARD_GREEN_HEX,
        fontStyle: 'bold',
        padding: { x: 14, y: 10 },
      })
      .setOrigin(0.5)
      .setName('edit-avatar')
      .setInteractive({ useHandCursor: true });
    edit.on('pointerup', () => {
      if (this.signedIn) this.scene.start('AvatarScene');
    });

    // The Avatar Shop, directly under it. Signed out there is no
    // profiles.avatar to write, so buying an icon would be buying a picture
    // you could never wear — both buttons stay hidden (spec 2026-09-21 §1).
    const shop = this.add
      .text(1105, 250, '🛒 AVATAR SHOP', {
        fontSize: '15px',
        color: '#1a140a',
        backgroundColor: '#f5c518',
        fontStyle: 'bold',
        padding: { x: 14, y: 10 },
      })
      .setOrigin(0.5)
      .setName('avatar-shop')
      .setInteractive({ useHandCursor: true });
    shop.on('pointerup', () => {
      if (this.signedIn) this.scene.start('AvatarShopScene');
    });

    // Hidden until we know there is a session to edit.
    edit.setVisible(false);
    shop.setVisible(false);
    void leaderboardSignedIn().then((yes) => {
      this.signedIn = yes;
      if (edit.active) edit.setVisible(yes);
      if (shop.active) shop.setVisible(yes);
    });
  }

  // ── Data ──────────────────────────────────────────────────────────────────

  private async refresh(): Promise<void> {
    const seq = ++this.requestSeq;
    this.showStatus('LOADING…');
    this.clearRows();

    const [rows, mine, signedIn] = await Promise.all([
      fetchTopBoard(this.board),
      fetchMyRank(this.board),
      leaderboardSignedIn(),
    ]);
    this.signedIn = signedIn;
    // A slower earlier request must not paint over a newer tab.
    if (seq !== this.requestSeq || !this.scene.isActive()) return;

    if (rows === null) {
      this.showStatus('LEADERBOARD UNAVAILABLE');
    } else if (rows.length === 0) {
      this.showStatus('NO RANKED PLAYERS YET');
    } else {
      this.showStatus('');
      this.renderRows(rows);
    }
    this.renderOwnRow(mine);
  }

  private showStatus(msg: string): void {
    this.statusText?.setText(msg).setVisible(msg.length > 0);
  }

  private clearRows(): void {
    this.list?.content.removeAll(true);
    this.list?.setContentHeight(0);
  }

  private renderRows(rows: LeaderboardRow[]): void {
    if (!this.list) return;
    rows.forEach((row, i) => {
      const container = this.buildRow(row, 0, i * ROW_PITCH, false, i);
      this.list!.content.add(container);
    });
    this.list.setContentHeight(rows.length * ROW_PITCH);
  }

  /**
   * The player's own standing, drawn with the SAME renderer as a list row so
   * the two cannot drift apart, and showing the true rank however deep it is
   * — #79 reads #79, not "unranked".
   */
  private renderOwnRow(mine: LeaderboardRow | null): void {
    this.ownRow?.destroy();
    this.ownRow = undefined;
    this.ownRowHint?.destroy();
    this.ownRowHint = undefined;

    if (mine) {
      this.ownRow = this.buildRow(mine, BOARD_X, 182, true);
      // King of the board (Diamond, spec 2026-10-29): #1 on ANY board,
      // the Capy Run board included. Awarded once, ever.
      if (mine.rank === 1) {
        try {
          const a = new RankService(saveStorage()).award('kingoftheboard');
          if (a) enqueueAchievementToast({ title: a.name, body: a.how, iconKey: a.icon });
        } catch (err) {
          console.warn('[LeaderboardScene] king of the board award failed', err);
        }
      }
      return;
    }
    this.ownRowHint = this.add
      .text(
        BOARD_X + BOARD_W / 2,
        208,
        !this.signedIn
          ? 'SIGN IN TO SEE YOUR RANK'
          : this.single
            ? 'FINISH AN ARCHIVE CAPY RUN TO JOIN THE BOARD'
            : 'PLAY A LEVEL TO JOIN THE BOARD',
        {
          fontSize: '16px',
          color: '#9aa6ad',
          fontStyle: 'italic',
          backgroundColor: '#15131f',
          padding: { x: 18, y: 12 },
        },
      )
      .setOrigin(0.5);
  }

  /**
   * One row: rank, avatar, name in the middle, value on the right. `own` tints
   * it so a player can find themselves at a glance.
   */
  private buildRow(
    row: LeaderboardRow,
    x: number,
    y: number,
    own = false,
    phase = 0,
  ): Phaser.GameObjects.Container {
    const c = this.add.container(x, y);

    const panel = this.add.graphics();
    panel.fillStyle(own ? 0x2b3a24 : 0x1b2430, 0.94);
    panel.fillRoundedRect(0, 0, BOARD_W, ROW_H, 10);
    panel.lineStyle(2, own ? 0x8ea87a : 0x2f3d4c, 1);
    panel.strokeRoundedRect(0, 0, BOARD_W, ROW_H, 10);
    c.add(panel);

    const mid = ROW_H / 2;

    c.add(
      this.add
        .text(RANK_CX, mid, formatRank(row.rank), {
          fontSize: '19px',
          color: rankColour(row.rank),
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    c.add(this.buildAvatar(AVATAR_CX, mid, row.avatar));

    c.add(
      this.add
        .text(NAME_CX, mid, row.username, {
          fontSize: '20px',
          color: own ? '#eaf5df' : '#e8ecef',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    const valueText = this.add
      .text(VALUE_RIGHT, mid, formatBoardValue(this.board, row.value), {
        fontSize: '19px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5);
    c.add(valueText);

    // The coins board carries the spinning coin, exactly as the menu balance
    // does. Placed off the measured text width so it never overlaps a big
    // number (see tests/e2e/overlap.spec.ts).
    if (this.board === 'coins') {
      const coin = this.add
        .image(VALUE_RIGHT - valueText.width - 18, mid, 'coin')
        .setDisplaySize(26, 26);
      // Rotate each row's frame order so the fifty coins spin OUT OF PHASE.
      // Started together they all hit the edge-on frame at the same instant,
      // and a column of slivers reads as a divider rule, not as coins.
      const offset = phase % COIN_SPIN_FRAMES.length;
      spinCoin(this, coin, [
        ...COIN_SPIN_FRAMES.slice(offset),
        ...COIN_SPIN_FRAMES.slice(0, offset),
      ]);
      c.add(coin);
    }

    return c;
  }

  /** The avatar: a circle with the chosen sprite in it, or a neutral disc. */
  private buildAvatar(cx: number, cy: number, key: string): Phaser.GameObjects.Container {
    const c = this.add.container(cx, cy);
    const ring = this.add.graphics();
    ring.fillStyle(0x0f1720, 1);
    ring.fillCircle(0, 0, AVATAR_R);
    ring.lineStyle(2, 0x5d7a4a, 1);
    ring.strokeCircle(0, 0, AVATAR_R);
    c.add(ring);
    // Fitted whole inside the circle by the shared helper — which also knows
    // that a shop icon's art is not its key, and that the title logo is wide.
    const art = drawAvatarIcon(this, key, AVATAR_R * 1.7, AVATAR_R * 1.7);
    if (art) c.add(art);
    return c;
  }
}

/** Gold, silver and bronze for the podium; plain for the rest. */
function rankColour(rank: number): string {
  if (rank === 1) return '#f5c518';
  if (rank === 2) return '#cfd3dc';
  if (rank === 3) return '#d08a4a';
  return '#9aa6ad';
}
